import { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import addIcon from "../../../images/addIcon.svg";
import { getBabyProfileContributors } from "../../../api";
import { LocalLoadingSpinner } from "../../../common/LoadingSpinner";
import { SmallTextColorProps } from "../../shared/SharedStyles";
import { ListItemWithImage } from "../../settings/common/shared/ListItemWithImage";
import { SharedButton } from "../../settings/common/shared/SharedButton";
import { useBabyContext } from "../../../context/BabyContext";
import { appImages } from "../../../common/constants";
import { colors } from "../../../styles/style";
import { AddTextModal } from "../../miletsones/components/common/AddTextModal";
import { AuthContext } from "../../../context/AuthContext";
import { capitalizeFirstLetter } from "../../../common/helpers";
import { FREE_SEATS } from "../../../common/plan";
import { useNavigate } from "react-router-dom";

interface UserInterface {
  id: string;
  email: string;
  name: string;
}

interface BabyUserInterface {
  baby_id: string;
  user_id: string;
  created_at: number;
  relation: string;
  user: UserInterface;
  entityType: "collaborator" | "invitation";
  receiver_email: string;
}
export const ProfileContributors = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [contributors, setContributors] = useState<BabyUserInterface[]>([]);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  const { selectedBabyId, babyHasActivePayment, isBabyAdmin, babyAdminName } =
    useBabyContext();
  const authContext = useContext(AuthContext);
  const { authUser } = authContext;
  const navigate = useNavigate();

  /* Pending invitations count against the free seats. They will become people
     the moment they are accepted, and not counting them would let a family
     invite their way past the limit two clicks at a time.

     Keyed on the BABY's entitlement: an invited collaborator on a baby the
     parent pays for must not be told the family is full. null means the answer
     has not arrived, and is treated as paid for the same reason as the locks. */
  const seatsUsed = contributors?.length ?? 0;
  const seatsFull = babyHasActivePayment === false && seatsUsed >= FREE_SEATS;

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await getBabyProfileContributors(
        selectedBabyId as string
      );
      setContributors(data);
    } catch (error) {
      console.error("Error fetching baby details:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adminUser = contributors?.find(
    // eslint-disable-next-line eqeqeq
    (c) => c.relation == "0" || c.relation === "admin"
  );
  const isAdmin = adminUser?.user_id === authUser?.id;
  return (
    <Container>
      <TitleText $color={colors.darkBrown} style={{ marginBottom: "16px" }}>
        Profile Contributors
      </TitleText>
      {babyHasActivePayment === false && (
        <SeatCount>
          {seatsUsed} of {FREE_SEATS} people
        </SeatCount>
      )}
      {!loading ? (
        contributors?.map((contributor, index) => (
          <ListItemWithImage
            key={`i-${index}`}
            itemData={{
              name:
                contributor.entityType === "collaborator"
                  ? contributor.user?.name
                  : contributor.receiver_email,

              email:
                contributor.entityType === "collaborator"
                  ? contributor.user.email
                  : "",
              descriptionText:
                contributor.entityType === "invitation"
                  ? `Invited as ${capitalizeFirstLetter(contributor.relation)}`
                  : contributor.relation.toString() === "0" ||
                      contributor.relation === "admin"
                    ? "Admin"
                    : contributor.relation,
              image: appImages.blankProfilePicture,
              id: contributor.user_id,
              userId: contributor.user_id,
              babyId: contributor.baby_id,
              deleteAllowed:
                !isAdmin || contributor.entityType === "invitation"
                  ? false
                  : true,
              entity: contributor.entityType,
            }}
            parentComponent="profile-contributors"
            onRefresh={() => fetchData()}
          />
        ))
      ) : (
        <LocalLoadingSpinner />
      )}

      {seatsFull && (
        <SeatLimitNote>
          {isBabyAdmin === false
            ? babyAdminName
              ? `The free plan includes you and two invited family members. Ask ${babyAdminName} to upgrade to invite more family.`
              : `The free plan includes you and two invited family members. Ask whoever set this baby up to upgrade to invite more family.`
            : `The free plan includes you and two invited family members. Pro invites the whole family — grandparents, siblings, everyone.`}
        </SeatLimitNote>
      )}
      <SharedButton
        iconSrc={addIcon}
        /* A visitor who cannot buy is not sent to a paywall they cannot use. */
        buttonText={
          seatsFull ? (isBabyAdmin === false ? "Full" : "See Pro") : "Add new"
        }
        $disabled={seatsFull && isBabyAdmin === false}
        onClick={() => {
          if (seatsFull) {
            if (isBabyAdmin === false) return;
            navigate("/settings");
            return;
          }
          setShowAddModal(true);
        }}
      />
      {showAddModal && (
        <AddTextModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={() => setShowAddModal(false)}
          initialText={"Add new contributor"}
          parentComponent="profileContributors"
        />
      )}
    </Container>
  );
};

const SeatCount = styled.span`
  display: block;
  margin: -8px 0 16px 0;
  color: ${colors.warmthBrown};
  font-size: 14px;
`;

const SeatLimitNote = styled.p`
  margin: 8px 0 12px 0;
  padding: 12px 16px;
  border-radius: 20px;
  background-color: ${colors.softPink};
  color: ${colors.darkBrown};
  font-size: 14px;
  line-height: 20px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 16px;
  gap: 16px;
`;

const TitleText = styled.h2<SmallTextColorProps>`
  color: ${(props) => props.$color};
  font-size: 20px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: -0.3px;
  align-self: flex-start; /* Align title to the left */
  margin-top: 15px;
  margin-bottom: 2px;
`;
